import { iCompanyOwner, tExtendedCompanyWithCounts } from "../../types";
import { isDateInFuture, willExpireWithinAWeek, formatDate } from "../../utils/helperFunctions";

interface CompanyOwnersTableProps {
    displayData: iCompanyOwner[];
    isLoading: boolean;
    onClickCompanyName: (companyId: string) => void;
    onClickSubscription: (owner: iCompanyOwner) => void;
    onClickExpiration: (owner: iCompanyOwner, company: tExtendedCompanyWithCounts) => void;
    onClickModules: (company: tExtendedCompanyWithCounts) => void;
    onClickEdit: (owner: iCompanyOwner) => void;
    onClickDelete: (owner: iCompanyOwner) => void;
    onResendInvite: (owner: iCompanyOwner) => void;
    resentOwnerIds: string[];
    currentOwnerId?: string;
}

const MODULE_DOTS = [
    { field: "initialStarterPackageEnrollment", color: "#3498db", label: "Inspections" },
    { field: "pmModuleEnrollment", color: "#e67e22", label: "PM" },
    { field: "tiresModuleEnrollment", color: "#9b59b6", label: "Tires" },
    { field: "costTrackingModuleEnrollment", color: "#1abc9c", label: "Cost" },
    { field: "trainingModuleEnrollment", color: "#e74c3c", label: "Training" },
    { field: "skillTrackingModuleEnrollment", color: "#34495e", label: "Skills" },
] as const;

const formatSubscriptionLabel = (type: string) => {
    switch (type) {
        case "paid-full-access": return "Full Access";
        case "paid-limited-access": return "Limited Access";
        case "free-demo": return "Free Demo";
        case "paused": return "Paused";
        default: return type;
    }
};

const ExpirationCell = ({ company, owner, onClick }: {
    company: tExtendedCompanyWithCounts;
    owner: iCompanyOwner;
    onClick: (owner: iCompanyOwner, company: tExtendedCompanyWithCounts) => void;
}) => {
    const exp = company.subscriptionExpiration;
    if (company.subscriptionType === "paused") {
        return (
            <span className="sam-expiration sam-expiration-paused">
                ⏸ {exp}
            </span>
        );
    }

    const today = formatDate(new Date());
    const isFuture = isDateInFuture(exp);
    const expiringSoon = isFuture && willExpireWithinAWeek(exp, today);

    let className = "sam-expiration-ok";
    let icon = "✓";
    if (!isFuture) { className = "sam-expiration-expired"; icon = "✕"; }
    else if (expiringSoon) { className = "sam-expiration-warn"; icon = "⚠"; }

    return (
        <span
            className={`sam-expiration ${className} sam-clickable`}
            onClick={() => onClick(owner, company)}
        >
            {icon} {exp}
        </span>
    );
};

export const CompanyOwnersTable = ({
    displayData,
    isLoading,
    onClickCompanyName,
    onClickSubscription,
    onClickExpiration,
    onClickModules,
    onClickEdit,
    onClickDelete,
    onResendInvite,
    resentOwnerIds,
    currentOwnerId,
}: CompanyOwnersTableProps) => {
    if (isLoading) {
        return <div className="sam-loading">Loading company owners...</div>;
    }

    if (!displayData || displayData.length === 0) {
        return <div className="sam-empty">No Company Owners</div>;
    }

    return (
        <div className="sam-table-wrapper">
            <table className="sam-table">
                <thead>
                    <tr>
                        <th>Company</th>
                        <th>Owner / Email</th>
                        <th>Status</th>
                        <th>Subscription</th>
                        <th>Expiration</th>
                        <th>Modules</th>
                        <th>Users</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {displayData.map((owner) => {
                        const company = owner.company?.[0];
                        return (
                            <tr key={owner.id}>
                                {/* Company Name */}
                                <td>
                                    {company ? (
                                        <span
                                            className="sam-company-link"
                                            onClick={() => onClickCompanyName(company.id)}
                                        >
                                            {company.name}
                                        </span>
                                    ) : (
                                        <span style={{ color: "var(--slate-gray)" }}>—</span>
                                    )}
                                </td>

                                {/* Owner / Email */}
                                <td>
                                    <div className="sam-owner-name">{owner.name}</div>
                                    <div className="sam-owner-email">{owner.email}</div>
                                </td>

                                {/* Status */}
                                <td>
                                    {owner.cognitoId ? (
                                        <span className="sam-badge sam-badge-confirmed">Confirmed</span>
                                    ) : resentOwnerIds.includes(owner.id || "") ? (
                                        <span className="sam-badge sam-badge-confirmed">Invite Sent</span>
                                    ) : (
                                        <span
                                            className="sam-badge sam-badge-unconfirmed"
                                            style={{ cursor: "pointer" }}
                                            title="Click to resend invite"
                                            onClick={() => onResendInvite(owner)}
                                        >
                                            Unconfirmed ✉
                                        </span>
                                    )}
                                </td>

                                {/* Subscription Type */}
                                <td>
                                    <span
                                        className="sam-clickable"
                                        onClick={() => onClickSubscription(owner)}
                                    >
                                        {formatSubscriptionLabel(owner.subscriptionType)}
                                    </span>
                                </td>

                                {/* Expiration */}
                                <td>
                                    {company ? (
                                        <ExpirationCell
                                            company={company}
                                            owner={owner}
                                            onClick={onClickExpiration}
                                        />
                                    ) : "—"}
                                </td>

                                {/* Module Dots */}
                                <td>
                                    {company ? (
                                        <div
                                            className="sam-module-dots"
                                            onClick={() => onClickModules(company)}
                                            title="Click to manage modules"
                                        >
                                            {MODULE_DOTS.map(({ field, color, label }) => {
                                                const isActive = !!(company as Record<string, unknown>)[field];
                                                return (
                                                    <span
                                                        key={field}
                                                        className={`sam-module-dot ${isActive ? "active" : "inactive"}`}
                                                        style={{ backgroundColor: color }}
                                                        title={`${label}: ${isActive ? "Active" : "Inactive"}`}
                                                    />
                                                );
                                            })}
                                        </div>
                                    ) : "—"}
                                </td>

                                {/* Users */}
                                <td>{company?.userCount ?? "—"}</td>

                                {/* Actions */}
                                <td>
                                    <div className="sam-actions">
                                        {company && (
                                            <button
                                                className="sam-action-btn"
                                                title="Manage modules"
                                                onClick={() => onClickModules(company)}
                                            >
                                                ⊞
                                            </button>
                                        )}
                                        <button
                                            className="sam-action-btn"
                                            title="Edit subscription"
                                            onClick={() => onClickEdit(owner)}
                                        >
                                            ✎
                                        </button>
                                        {owner.id !== currentOwnerId && (
                                            <button
                                                className="sam-action-btn danger"
                                                title="Delete"
                                                onClick={() => onClickDelete(owner)}
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};
